import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldLabel, FieldTitle } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { parseError } from '@/lib/errors'
import {
  type EmailTemplate,
  useSetDefaultEmailTemplate,
  useUpdateEmailTemplate,
} from '@/models/emailTemplate'
import { DeleteTemplateButton } from './DeleteTemplateButton'
import { TemplateScopeNote } from './TemplateScopeNote'

interface TemplateEditorProps {
  template: EmailTemplate | null
  /** A global template or the rep's own: they can also delete it or make it their default. */
  canManage: boolean
}

// Keyed by template id: a remount re-reads state, and MDXEditor reads markdown only once.
export function TemplateEditor({ template, canManage }: TemplateEditorProps) {
  const [name, setName] = useState(() => template?.name ?? '')
  const [subject, setSubject] = useState(() => template?.subject ?? '')
  const [body, setBody] = useState(() => template?.body ?? '')

  const updateMutation = useUpdateEmailTemplate()
  const setDefaultMutation = useSetDefaultEmailTemplate()

  if (!template) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 text-sm">
        <p>Templates save a subject and body to reuse when you email or text a lead.</p>
        <p>Create one with New.</p>
      </div>
    )
  }

  const { id } = template
  const dirty = name !== template.name || subject !== template.subject || body !== template.body

  function handleSave() {
    updateMutation.mutate({ id, name, subject, body }, { onError: (err) => toast.error(parseError(err)) })
  }

  function handleSetDefault() {
    setDefaultMutation.mutate(id, { onError: (err) => toast.error(parseError(err)) })
  }

  return (
    <div className="flex flex-col gap-4">
      <TemplateScopeNote template={template} canManage={canManage} />
      <Field>
        <FieldLabel htmlFor="template-name" optional>
          Name
        </FieldLabel>
        <Input
          id="template-name"
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          placeholder={subject || 'Untitled'}
        />
        <FieldDescription>Recipients never see the name. Leave it blank to use the subject.</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="template-subject">Subject</FieldLabel>
        <Input id="template-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field>
        <FieldTitle>Body</FieldTitle>
        <MarkdownEditor value={body} onChange={setBody} />
        <FieldDescription>
          <code className="rounded bg-neutral-100 px-1">{'{driver_name}'}</code> is replaced with the lead’s
          first name.
        </FieldDescription>
      </Field>
      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <div>{canManage && <DeleteTemplateButton template={template} />}</div>
        <div className="flex gap-2">
          {canManage && !template.isDefault && (
            <Button
              variant="outline"
              onClick={handleSetDefault}
              loading={setDefaultMutation.isPending ? 'Saving…' : undefined}
            >
              Set as default
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!dirty}
            loading={updateMutation.isPending ? 'Saving…' : undefined}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
